import type { Appearance } from '@unifyapps/defs/blocks/Container/types';
import type { CustomBlockEntity, CustomBlockManifest } from '@unifyapps/defs/blocks/Custom/types';
import { stripEmptyOneOfFields } from '@unifyapps/defs/blocks/common/stripEmptyOneOfFields';
import type { VariantSchema } from '@unifyapps/defs/blocks/common/themeVariantsSchema';
import type { ThemeVariantDimension } from '@unifyapps/defs/theme/types';
import type { Action } from '@unifyapps/defs/types/action';
import type {
  BlockBehavior,
  BlockCompositionData,
  BlockId,
  BlockStateUnionType,
  BlockType,
  BlockTypeUnionType,
  ComponentsUnionType,
  ComputedBlockStateUnionType,
} from '@unifyapps/defs/types/block';
import type { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { EventTypeEnum } from '@unifyapps/defs/types/event';
import type { ApplicationType } from '@unifyapps/defs/types/interface';
import type { DependsOnType, InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { AssetDescriptor } from '@unifyapps/network/asset-cache/types';
import type { SnackbarProps } from '@unifyapps/ui/components/Snackbar/types';
import type { WritableDraft } from 'immer';
import type { FC } from 'react';
import type { JSONTreeSchemaObject } from '../../types';
import type { BlockComponentProps } from '../components/BlockRenderer/types';
import type BlocksRegistry from '../context/RegistryProvider/BlocksRegistry';
import type { GlobalStateStoreType } from '../stores/GlobalStateStore';
import type { PropertyPanelHistoryItem } from '../stores/InterfaceStore/builder';
import type { InsertBlockContext } from '../stores/InterfaceStore/builder/actions/insertBlock';
import type { PanelResolver } from './panelNavigation';

export type ComponentType = React.ComponentType<
  BlockComponentProps<ComponentsUnionType, BlockStateUnionType, ComputedBlockStateUnionType>
>;

export type BlockInfo = {
  name: string;
  illustration: string;
  icon: string;
  category:
    | 'Layout'
    | 'Button'
    | 'Rich'
    | 'Basic'
    | 'Repeatables'
    | 'Commerce'
    | 'Navigation'
    | 'Form'
    | 'Copilot'
    | 'Custom';
  /**
   * When searching for a block, these keywords will be used to match the block
   */
  keywords: string;
  isCompositeBlock?: boolean;
  /**
   * This is the default layout for the Container in which the block will be wrapped
   */
  defaultLayout?: Appearance['variant'];
  /**
   * the slot to pick when something is being pasted
   */
  mainSlot?: string;
  /**
   *  when the properties panel is opened, the block details will be rendered in a flat manner without tabs
   */
  flatLayout?: boolean;

  /**
   * is detached block - metadata flag for block picker/UI.
   * Runtime behavior is controlled by BlockBehavior.detachFromLayout.
   */
  isDetachedBlock?: boolean;

  /**
   * When set, this block only appears in the block picker when any ancestor
   * in the hierarchy matches one of the specified component types.
   */
  visibleIfAncestorHasType?: BlockTypeUnionType[];

  /**
   * When set, this block is HIDDEN from the picker if any ancestor in the hierarchy matches
   * one of the specified component types. Mirror of `visibleIfAncestorHasType` for the
   * opposite case; used by `SlotOutlet` so it cannot be nested inside another `SlotOutlet`'s
   * default-children subtree (spec §4.2 invariant 4 + §5.2 picker restriction).
   */
  hiddenIfAncestorHasType?: BlockTypeUnionType[];

  // When set, this block only appears in the block picker for interfaces whose id is in this list
  allowedInterfaceIds?: string[];
};

export function isBlockAllowedForInterface(
  allowedInterfaceIds: string[] | undefined,
  interfaceId: string | undefined,
): boolean {
  if (!allowedInterfaceIds) return true;
  return interfaceId !== undefined && allowedInterfaceIds.includes(interfaceId);
}

export type ContentSchemaParams = {
  interfaceId: string;
  device?: DeviceVariantType;
  isAppConfigHidden?: boolean;
  applicationType?: ApplicationType;
  themeVariantsConfig?: ThemeVariantDimension[];
  modules?: InterfacePageEntity[];
};

export type StateSchemaOptions = {
  getDataSourceOutputSchema?: (params: {
    block: BlockType;
    data: string;
    schemas?: JSONTreeSchemaObject[];
  }) => object | undefined | Promise<object | undefined>;
  modules?: InterfacePageEntity[] | undefined;
  activeBlockId?: BlockId;
  isBlockAncestorOfActiveBlockFn?: (blockId: BlockId) => boolean;
  isBlockAncestorOfActiveBlock?: boolean;
  getGlobalStateStoreState?: () => GlobalStateStoreType;
  selectedEventId?: EventTypeEnum;
  propertyPanelActiveState?: PropertyPanelHistoryItem;
  schemas?: JSONTreeSchemaObject[];
};

export abstract class BlockEditorDefinition {
  getCompositionData?: (device?: DeviceVariantType) => BlockCompositionData;

  getEditorComponent?: () => FC<{
    shouldRunPropertyChangeEffects: boolean;
  }>;

  getContentSchemaTransformer?: () => {
    toFormData: (block: BlockType) => object;
    toBlockData: ({
      draftBlock,
      formData,
    }: {
      draftBlock: WritableDraft<BlockType>;
      formData: object;
    }) => void;
  };

  getBlockAppearanceSchemaTransformer?: () => {
    toFormData: (block: BlockType) => object;
    toBlockData: ({
      draftBlock,
      formData,
    }: {
      draftBlock: WritableDraft<BlockType>;
      formData: object;
    }) => void;
  };

  controlMethodSchemaGetter?: () => (
    methodName: string,
    block: BlockType,
    options?: { modules?: InterfacePageEntity[] },
  ) => {
    schema: object;
    uiSchema: object;
  };

  /**
   * This is the state schema that we show in the input data panel for the block
   */
  stateSchema?: (
    block: BlockType,
    options: StateSchemaOptions,
  ) =>
    | {
        schema?: object;
      }
    | Promise<{
        schema?: object;
      }>;

  /**
   * optional resolver for deep-link panel navigation.
   * given a target (JSON path, fieldId, etc.), returns the full panel stack
   * needed to display that target in the properties panel.
   */
  panelResolver?: PanelResolver;

  /**
   * Returns the entity references that this block contributes to the current page.
   * Each entry carries an `id` and an `entityType` discriminator; the scanner
   * dedupes by composite key (`entityType` + `id`) across all blocks on the page.
   * Called by scanEmbeddedEntityReferences on each block change; not called
   * during render.
   * Optional — block types that don't reference external entities can omit it.
   */
  getEmbeddedEntityReferences?: (block: BlockType) => { id: string; entityType: string }[];

  abstract get type(): BlockTypeUnionType;

  /**
   * This is the information about the block which is used to display in the block picker (in the left panel)
   */
  abstract get info(): BlockInfo;

  /**
   * This is a content schema used to render a content form in a properties panel of the block (in the right panel)
   */
  abstract getContentSchema(
    block: BlockType,
    params: ContentSchemaParams,
  ): {
    schema?: object;
    uiSchema?: object;
  };

  /**
   * This is the initial form data for the block which is used to create the block via setting data in the form (in the right panel)
   */
  abstract getInitialFormData(
    device?: DeviceVariantType,
    context?: InsertBlockContext,
  ): ComponentsUnionType;

  /**
   * This is an appearance schema used to render an appearance form in a properties panel of the block (in the right panel)
   */
  abstract getAppearanceSchema(
    block: BlockType,
    params?: {
      getGlobalStateStoreState: () => GlobalStateStoreType;
      applicationType?: ApplicationType;
      device?: DeviceVariantType;
      /** variant dimensions from theme entity, if available for this component */
      themeVariantsConfig?: ThemeVariantDimension[];
      /** typography's variant+weight overrides — blocks opt in by reading this */
      typographyVariantOverrides?: Record<string, VariantSchema>;
    },
  ): {
    schema?: object;
    uiSchema?: object;
  };

  /**
   * optional — maps a block instance to its theme-manager component id.
   * blocks whose theme id depends on a runtime prop (e.g. Icon forks into
   * `icon` / `featured-icon` via appearance.variant) implement this; others
   * fall back to `componentType.toLowerCase()` in the registry helper.
   */
  getThemeComponentId?(block: BlockType): string;
}

/**
 * Computes the static list of override-eligible field keys for a block's content
 * schema, by reading `'ui:exposeToComponentOverrides': true` markers in the uiSchema.
 *
 * Pure: derives only from the static content schema. Does not read store
 * state. Defers to `getContentSchema` so block-specific schema-shaping
 * (e.g. `isAppConfigHidden`) is honoured.
 *
 * Hidden-variant fields (theme `oneOf` stripped to empty) are excluded from the
 * result via `stripEmptyOneOfFields` — keeps the override picker in lockstep
 * with the property panel.
 *
 * Implemented as a free function (not a class method) because many block
 * subclasses narrow `getContentSchema`'s `params` type; inheriting a
 * concrete method that calls `this.getContentSchema(block, params)` with
 * the wider abstract signature breaks structural subtype compatibility.
 */
export function getExposedComponentOverridesFields(
  def: BlockEditorDefinition,
  block: BlockType,
  params: {
    interfaceId: string;
    device?: DeviceVariantType;
    isAppConfigHidden?: boolean;
    applicationType?: ApplicationType;
  },
): string[] {
  const { uiSchema } = stripEmptyOneOfFields(def.getContentSchema(block, params));
  const typedUiSchema = (uiSchema ?? {}) as Record<
    string,
    { 'ui:exposeToComponentOverrides'?: boolean } | undefined
  >;
  return Object.keys(typedUiSchema).filter(
    (key) => typedUiSchema[key]?.['ui:exposeToComponentOverrides'] === true,
  );
}

export type BlockControlMethodHelpers = {
  closeSnackbar: () => void;
  showSnackbar: (props: SnackbarProps) => void;
  getComputeContext: () => Record<string, unknown>;
  getComputedFilters: (
    filters: Record<string, unknown>,
    context: Record<string, unknown>,
  ) => object;
  getComputedData: <T>(
    data: Record<string, unknown> | string | unknown[],
    context: Record<string, unknown>,
  ) => T;
  onExportBlock?: (blockId: BlockId) => Promise<void>;
  targetBlockId?: BlockId;
  /** Sub-element the interaction happened on, when the caller supplied one. Preferred over `targetBlockId` for anchoring. */
  anchorId?: string;
  block: BlockType;
  registry: BlocksRegistry;
};

export type BlockControlMethod = {
  id: string;
  label: string;
  /**
   * Optional human-readable explanation of what the method does.
   * Rendered as inline caption text under the label in the method picker.
   */
  description?: string;
  method: (
    component: BlockStateUnionType | undefined,
    action: Action,
    blockRef: unknown,
    helpers: BlockControlMethodHelpers,
  ) => BlockStateUnionType | Promise<BlockStateUnionType | undefined> | Promise<void>;
  /**
   * If true, this method will be hidden from the dropdown unless it's the current selected value.
   * This is useful for backward compatibility when deprecating methods.
   */
  hiddenUnlessSelected?: boolean;
};

export type ImplicitDependencyGroup = {
  /**
   * turn on this flag when the block uses useIsEntitySuspended hook
   */
  isEntitySuspended?: boolean;
  /**
   * turn on this flag when the block uses useDataSourceResponses hook
   */
  usingDataSourcesResponse?: boolean;
  /**
   * turn on this flag when you want to implicitly depend on the user context via ComputeContextHelper.getUserContext or any other way
   */
  usingUserContext?: boolean;
  /**
   * turn on this flag when the block wants to implicitly access page functions state, for example for default value loading indicator
   */
  usingPageFunctionLoaders?: boolean;
};

export abstract class BlockStateDefinition {
  abstract getComponent: () => ComponentType;

  useBlockRef?: boolean;
  /**
   * This function is called to preload the block
   */
  preload?: () => {
    /**
     * runner is used to indicate that the block is needed to be preloaded when page is in runner mode,
     * for example preloading the form block so that the flash of suspense is not visible when user lands on a page that has form block as major UI element.
     */
    runner?: boolean;
    blockPreload?: () => Promise<{
      default: ComponentType;
    }>;
    miscellaneous?: (block?: BlockType) => (() => Promise<void>)[];
  };
  /*
   * This is for the slot order of the block in which order it should render in hierarchy
   */
  getSlotOrder?: (block: BlockType) => { order: string[] };
  /**
   * when computing a block state, we want to skip running computation over the dynamic properties,
   * this function helps in that
   * might be removed when in future we get
   * and set the block state based on where the dynamic values are present statically
   */
  getKeysToOmitInComputation?: () => string[];
  /**
   * This function gives the list of active target ids for the block to remove stale events
   */
  getEventTargetIds?: (block: BlockType) => string[];
  /**
   * This function gives the list of control methods for the block
   */
  getBlockControlMethods?: (
    block: BlockType,
    applicationType: ApplicationType | undefined,
    options?: {
      modules?: InterfacePageEntity[];
    },
  ) => BlockControlMethod[];

  getBlockBehavior?: () => BlockBehavior;

  /**
   * when computing the dependencies of a block, we use this function to get extra implicit dependencies of a block
   * that is not configured by the user, but required because of the configuration of the block
   *
   */
  getImplicitDependencies?: (
    block: BlockType,
    activeInterfacePageId: string,
  ) => DependsOnType[] | undefined;

  /**
   * when computing the dependencies of a block,
   * we use this function to know which functionality does the block uses from the system, so we add dependencies accordingly
   */
  getImplicitDependencyGroups?: () => ImplicitDependencyGroup;

  /**
   * This type is used to identify the block in JSON
   */
  abstract get type(): BlockTypeUnionType;

  /**
   * This function is called in builder mode everytime we make change in the block's properties panel
   */
  abstract get initialStateGetter(): (block: BlockType) => BlockStateUnionType;

  /**
   * This function is called to get the version of the block
   */
  getBlockVersion: () => number;

  addManifest?: (customBlockSnapshot: CustomBlockEntity) => void;
  getManifest?: (version?: string) => CustomBlockManifest | undefined;
  getAllManifests?: () => CustomBlockManifest[];

  // Returns block-specific assets that should be prefetched/cached for
  // smoother rendering. Async so future implementations can do HEAD
  // requests or metadata sniffs without a breaking signature change.
  // Today every extractor resolves synchronously and wraps in
  // `Promise.resolve`.
  getCachableAssets?: (block: BlockType) => Promise<AssetDescriptor[] | undefined>;
}
